import React, { FC, useMemo } from 'react';
import { Menu } from 'antd';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import Icon, {
    BarChartOutlined,
    MenuOutlined,
    SettingOutlined,
    DashboardOutlined,
    TeamOutlined,
    AppstoreOutlined,
    CodeOutlined,
    UserDeleteOutlined,
    UsergroupAddOutlined,
} from '@ant-design/icons';
import { MdMeetingRoom } from 'react-icons/md';
import { IoServerOutline, IoTrophyOutline } from 'react-icons/io5';
import { MdForkRight } from 'react-icons/md';
import { RiCoinsLine, RiAwardFill } from 'react-icons/ri';
import { MdRepeat } from 'react-icons/md';

import logo from '../../assets/images/logo.png';

import style from './sidebar.styles';
import { ROUTES } from '../../router/url';
import { GameSvg } from '../../assets/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/reducers';
import { getUserRole, UserRoleName } from '../../utils/authRole';

const MenuWrapper = styled.div`
    ${style}
`;

interface SideBarProps {
    onPress?: () => void;
}

interface MenuItemConfig {
    key: string;
    label: string;
    route: string;
    icon: React.ReactNode;
    roles: UserRoleName[];
}

const menuItems: MenuItemConfig[] = [
    { key: 'dashboard', label: 'Dashboard', route: ROUTES.DASHBOARD, icon: <DashboardOutlined />, roles: ['ADMIN'] },
    {
        key: 'insights',
        label: 'Insights',
        route: ROUTES.INSIGHTS,
        icon: <BarChartOutlined />,
        roles: ['ADMIN', 'MARKETING'],
    },
    {
        key: 'user-insights',
        label: 'User Insights',
        route: ROUTES.USER_INSIGHTS,
        icon: <TeamOutlined />,
        roles: ['ADMIN', 'MARKETING'],
    },
    // {
    //     key: 'payments',
    //     label: 'Payments',
    //     route: ROUTES.PAYMENTS,
    //     icon: (
    //         <span className="icon-span">
    //             <RiCoinsLine className="extra-icon" />
    //         </span>
    //     ),
    //     roles: ['ADMIN'],
    // },
    { key: 'category', label: 'Category', route: ROUTES.CATEGORY, icon: <AppstoreOutlined />, roles: ['ADMIN'] },
    { key: 'developers', label: 'Developers', route: ROUTES.DEVELOPER, icon: <CodeOutlined />, roles: ['ADMIN'] },
    { key: 'games', label: 'Games', route: ROUTES.GAMES, icon: <Icon component={GameSvg} />, roles: ['ADMIN'] },
    {
        key: 'streaks',
        label: 'Streaks',
        route: ROUTES.STREAKS,
        icon: (
            <span className="icon-span">
                <RiAwardFill className="extra-icon" />
            </span>
        ),
        roles: ['ADMIN'],
    },
    // {
    //     key: 'subscriptions',
    //     label: 'Subscriptions',
    //     route: ROUTES.SUBSCRIPTIONS,
    //     icon: (
    //         <span className="icon-span">
    //             <MdRepeat className="extra-icon" />
    //         </span>
    //     ),
    //     roles: ['ADMIN'],
    // },
    // {
    //     key: 'subscribers',
    //     label: 'Subscribers',
    //     route: ROUTES.SUBSCRIBERS,
    //     icon: <UsergroupAddOutlined />,
    //     roles: ['ADMIN'],
    // },
    {
        key: 'blocked-users',
        label: 'Blocked Users',
        route: ROUTES.BLOCKED_USERS,
        icon: <UserDeleteOutlined />,
        roles: ['ADMIN'],
    },
    {
        key: 'rooms',
        label: 'Rooms',
        route: ROUTES.ROOMS,
        icon: (
            <span className="icon-span">
                <MdMeetingRoom className="extra-icon" />
            </span>
        ),
        roles: ['ADMIN'],
    },
    {
        key: 'leagues',
        label: 'Leagues',
        route: ROUTES.LEAGUES,
        icon: (
            <span className="icon-span">
                <IoTrophyOutline className="extra-icon" />
            </span>
        ),
        roles: ['ADMIN'],
    },
    {
        key: 'knockout',
        label: 'Knockouts',
        route: ROUTES.KNOCKOUT,
        icon: (
            <span className="icon-span">
                <MdForkRight className="extra-icon" />
            </span>
        ),
        roles: ['ADMIN'],
    },
    { key: 'users', label: 'Users', route: ROUTES.USERS, icon: <TeamOutlined />, roles: ['ADMIN'] },
    {
        key: 'trackers',
        label: 'Trackers',
        route: ROUTES.TRACKER,
        icon: (
            <span className="icon-span">
                <IoServerOutline className="extra-icon" />
            </span>
        ),
        roles: ['ADMIN'],
    },
    { key: 'settings', label: 'Settings', route: ROUTES.SETTINGS, icon: <SettingOutlined />, roles: ['ADMIN'] },
];

const SideBar: FC<SideBarProps> = ({ onPress }) => {
    const _location = useLocation().pathname;
    const user: any = useSelector<any>((state: RootState) => state.auth.data);
    const role = getUserRole(user);

    const keys = useMemo(() => {
        return _location.split('/').filter((a) => !!a);
    }, [_location]);

    const visibleMenuItems = useMemo(() => menuItems.filter((item) => item.roles.includes(role)), [role]);

    return (
        <MenuWrapper>
            <Menu mode="inline" theme={'light'} selectedKeys={[keys[0]]} style={{ height: '100%', borderRight: 0 }}>
                <div className="header">
                    <MenuOutlined onClick={onPress} />
                    <img className={'logo'} src={logo} alt={'weplay-logo'} />
                </div>
                {visibleMenuItems.map((item) => (
                    <Menu.Item key={item.key} icon={item.icon}>
                        <Link to={item.route} />
                        {item.label}
                    </Menu.Item>
                ))}
            </Menu>
        </MenuWrapper>
    );
};

export default SideBar;
